/* ===== game/boss-gap-fx.js — 보스의 틈 · 방어 자세를 몸에 보이게 — 글 알림 없이 몸과 체력 막대로만(사용자 결정) ===== */
/* 비틀거림 = 머리 위를 도는 별 셋 · 과열 = 달아오른 빛 + 김 · 균열 = 몸에 번진 금 + 떨어지는 조각 · 방어 자세 = 앞을 덮는 막.
   ★ 손님 화면도 같은 그림 — 값은 호스트가 보낸 gapK · gapT · braceV(game/net.ts). 사연: docs/code-history.md#h181 */
import { TAU } from '../../engine/core/math.js';
import { mixin } from '../../engine/core/mixin.js';
import { BOSS_MOVES, GAP_KIND } from '../data/bossmoves.js';
import { Sprites } from '../sprites.js';
import { Enemy } from '../entity.js';
import { Game } from '../game.js';
import { bodyCrack, hs } from './boss-hazard-art.js';

/** 금 그물 — 한 점에서 갈래 n 이 잘게 꺾이며 뻗는다(어두운 틈 + 갈래 빛깔 속불). grow 0~1 로 자란다 */
function crackNet(c: CanvasRenderingContext2D, sd: number, cx: number, cy: number, R: number, grow: number, col: string, n = 7) {
  for (const [cl, lw] of [['rgba(18,12,8,.92)', 3.6], [col, 1.3]] as [string, number][])
    for (let i = 0; i < n; i++) {
      const a = i / n * TAU + (hs(sd, i) - 0.5) * 0.7, L = R * (0.45 + hs(sd, i + 20) * 0.6) * grow;
      if (L > 2) bodyCrack(c, sd * 7 + i, cx, cy, a, L, cl, lw);
    }
}
let CV: HTMLCanvasElement | null = null;

export const BossGapFxPart: Bag = {

  /** 틈이 열리는 순간 — 갈래 빛깔의 고리 · 흔들림, 헛손질이면 한 겹 더 */
  gapBurst(e: any) {
    const K = GAP_KIND[e.gapK]; if (!K) return;
    /* ★ 틈이 열릴 때 고리 둘(ringFx)을 그렸더니 모든 보스 · 모든 기술 뒤에 같은 '마방진'이 떴다(점검판 2026-11-03 여덟 보스) — 그 보스의 결로 터진다 */
    this.tfxBurst(e, e.gapMiss ? 34 : 22, e.gapMiss ? 300 : 220);
    this.shake = Math.max(this.shake || 0, e.gapMiss ? 9 : 5);
    this.sfxAt && this.sfxAt(e.gapK === 'stagger' ? 'sk_quake' : e.gapK === 'heat' ? 'sk_fire' : 'hit_crit', e.cx / 22, e.cy / 22);
  },

  /** 균열 틈 — 그 몸 그림 위에만 금이 번진다(시트 칸을 따로 그려 그 모양으로 오린다). drawEnemy 가 몸을 그린 바로 다음에 부른다.
      ★ 네모 금 그림을 몸 위에 붙이면 몸 밖까지 곧은 줄이 그어졌다(사용자 지적) — 사연: docs/code-history.md#h227 */
  enemyCrack(c: CanvasRenderingContext2D, e: any, key: string, x: number, y: number) {
    if (!(e.gapT > 0 && e.gapK === 'crack') || !e.boss) return;
    const m = Sprites.meta && (Sprites.meta.characters.sheets[key] || Sprites.meta.bosses.sheets[key]); if (!m) return;
    const fw = m.frameW, fh = m.frameH, ox = m.ox || 0, oy = m.oy || 0;
    if (!CV) CV = document.createElement('canvas');
    if (CV.width < fw || CV.height < fh) { CV.width = Math.max(CV.width, fw); CV.height = Math.max(CV.height, fh); }
    const g = CV.getContext('2d')!; g.setTransform(1, 0, 0, 1, 0, 0); g.globalCompositeOperation = 'source-over'; g.clearRect(0, 0, CV.width, CV.height);
    if (!Sprites.draw(g, key, this.enemyFrame(e), -ox, -oy, e.facing < 0)) return;
    const C = this.crackOf(e, fw, fh), K = GAP_KIND[e.gapK];
    g.globalCompositeOperation = 'source-atop';
    crackNet(g, C.sd, C.ox, C.oy, Math.max(fw, fh) * 0.55, Math.min(1, (this.time - C.t0) / 0.35), K ? K.c : '#cfe8ff');
    c.save(); c.globalAlpha = Math.min(1, e.gapT * 3); c.drawImage(CV, 0, 0, fw, fh, x + ox, y + oy, fw, fh); c.restore();
    e._crkAt = this.time;
  },
  /** 이번 균열의 씨 · 시작점(몸 위쪽 가운데 언저리) — 틈이 새로 열릴 때마다 다시 */
  crackOf(e: any, w: number, h: number) {
    if (!e._crk || e._crk.m !== e.gapMax) e._crk = { m: e.gapMax, sd: 1 + Math.floor(Math.random() * 9999), u: 0.35 + Math.random() * 0.3, v: 0.3 + Math.random() * 0.25, t0: this.time };
    const C = e._crk; return { sd: C.sd, ox: w * C.u, oy: h * C.v, t0: C.t0 };
  },

  /** 몸 위의 틈 · 방어 자세 — drawMobFx 다음(같은 카메라) */
  drawBossGaps(c: CanvasRenderingContext2D, camX: number, camY: number) {
    const t = this.time;
    for (const e of this.ents || []) {
      if (!(e instanceof Enemy) || e.dead || !e.boss) continue;
      if (e.ghost && e.gapT > 0) e.gapT -= 1 / 60;                    // 손님 — 받은 남은 시간을 그림 박자로 줄인다
      const x = e.cx - camX, y = e.cy - camY, R = Math.max(e.w, e.h) * 0.62;
      if (x < -R * 2 || y < -R * 2 || x > this.W + R * 2 || y > this.H + R * 2) continue;
      const col = (BOSS_MOVES[e.type] || {}).c || '#bcd8f4';
      c.save();
      if (e.bracing()) {                                              // 방어 자세 — 앞쪽 몸 가장자리에 보스 빛깔이 고인다(조각은 game/boss-theme-fx)
        const f = e.facing || 1, ex = x + f * e.w * 0.45, a = 0.22 + Math.sin(t * 9) * 0.08;
        const g = c.createLinearGradient(ex - f * e.w * 0.3, 0, ex + f * 18, 0);
        g.addColorStop(0, col + '00'); g.addColorStop(1, col + Math.round(a * 200).toString(16).padStart(2, '0'));
        c.globalCompositeOperation = 'lighter'; c.fillStyle = g;
        c.beginPath(); c.ellipse(ex, y, e.w * 0.14, e.h * 0.42, 0, 0, TAU); c.fill();
        c.globalCompositeOperation = 'source-over';
      }
      if (e.gapT > 0) {
        const K = GAP_KIND[e.gapK], k = Math.min(1, e.gapT / Math.max(0.3, e.gapMax || 1)), fade = Math.min(1, e.gapT * 3);
        if (e.gapK === 'stagger') {                                   // 별 셋이 머리 위를 돈다 — 기울어진 타원
          const hy = e.y - camY - 10;
          for (let i = 0; i < 3; i++) {
            const a = t * 5 + i * TAU / 3, sx = x + Math.cos(a) * e.w * 0.42, sy = hy + Math.sin(a) * 7;
            c.globalAlpha = fade * (Math.sin(a) > 0 ? 1 : 0.55); c.fillStyle = i ? '#ffe8a0' : '#ffffff';
            c.beginPath();
            for (let j = 0; j < 10; j++) { const r = j % 2 ? 3.2 : 8, b = j * Math.PI / 5 + t * 3; c.lineTo(sx + Math.cos(b) * r, sy + Math.sin(b) * r); }
            c.closePath(); c.fill();
          }
        } else if (e.gapK === 'heat') {                               // 달아오른 빛 — 몸 안쪽이 붉게 · 김이 오른다
          c.globalCompositeOperation = 'lighter';
          const g = c.createRadialGradient(x, y, 2, x, y, R * 1.1);
          g.addColorStop(0, 'rgba(255,150,70,' + (0.45 * fade) + ')'); g.addColorStop(1, 'rgba(255,90,30,0)');
          c.fillStyle = g; c.fillRect(x - R * 1.1, y - R * 1.1, R * 2.2, R * 2.2);
          if (Math.random() < 0.5) this.mfxAdd({ k: 'smoke', x: e.cx + (Math.random() - 0.5) * e.w * 0.7, y: e.y + e.h * 0.2, vx: (Math.random() - 0.5) * 20, vy: -60, life: 0.9, r: 5, c: '200,190,185', c2: '240,235,230' });
          if (Math.random() < 0.4) this.mfxAdd({ k: 'ember', x: e.cx + (Math.random() - 0.5) * e.w, y: e.cy, vx: (Math.random() - 0.5) * 60, vy: -90, life: 0.6, r: 2, c: '255,130,50', c2: '255,226,150' });
        } else if (e.gapK === 'crack') {                              // 몸에 번진 금 — 흰 금 그림, 남은 시간만큼 짙게
          if ((e as any)._crkAt !== this.time) {                            // 몸 그림이 시트가 아닌 보스(뒷벽 · 조각) — 판정 상자 타원 안에만
            const C = this.crackOf(e, e.w, e.h);
            c.globalAlpha = fade; c.beginPath(); c.ellipse(x, y, e.w * 0.5, e.h * 0.5, 0, 0, TAU); c.clip();
            crackNet(c, C.sd, x - e.w / 2 + C.ox, y - e.h / 2 + C.oy, Math.max(e.w, e.h) * 0.6, Math.min(1, (t - C.t0) / 0.35), K.c);
          }
          void k;
          if (Math.random() < 0.25) this.mfxAdd({ k: 'mote', x: e.cx + (Math.random() - 0.5) * e.w, y: e.cy, vx: (Math.random() - 0.5) * 80, vy: -40, life: 0.7, r: 2.2, c: '200,230,255', c2: '255,255,255' });
        }
        if (e.gapMiss && Math.random() < 0.3) this.mfxAdd({ k: 'smoke', x: e.cx + (Math.random() - 0.5) * e.w, y: e.y + e.h - 4, vx: (Math.random() - 0.5) * 60, vy: -20, life: 0.8, r: 6, c: '200,190,170', c2: '240,232,220' });   // 헛손질 — 발밑 먼지(고리 대신)
      }
      c.restore();
    }
  }
};
mixin(Game.prototype, BossGapFxPart, true);
